/**
 * Per-instance orchestrator: one SDK client, one media grab, one upload pipeline.
 * Owned by the React component that mounts it (via useRef in useProctoring).
 * Created on exam mount, destroyed on exam unmount — standard React lifecycle.
 * pause/resume are advisory wrapper state only — the SDK has no media pause.
 */

import {
    BlockerController,
    ProctoringClient,
    type BlockerState,
    type ProctoringCallbacks,
    type ProctoringState,
    type SDKObservabilityConfig,
} from '@telusinternationalai/proctoring-sdk'
import { errorProctoring, logProctoring, warnProctoring } from './debugLog'
import { ProctoringEventBus } from './eventBus'
import {
    enumerateDevices,
    isBrowserSupported,
    isMobileDevice,
    queryPermissions,
} from './permissions'
import type { ProctoringConfigInput, ProctoringPhase, ProctoringSnapshot } from './types'

class StateStore<T extends object> {
    private state: T
    private readonly listeners = new Set<() => void>()

    constructor(initialState: T) {
        this.state = initialState
    }

    getState(): T {
        return this.state
    }

    setState(partial: Partial<T>): void {
        this.state = { ...this.state, ...partial }
        this.listeners.forEach(l => l())
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    clear(): void {
        this.listeners.clear()
    }
}

const INITIAL_SDK_STATE: ProctoringState = {
    status: 'idle',
    permissionsGranted: false,
}

const createInitialSnapshot = (): ProctoringSnapshot => ({
    phase: 'uninitialized',
    sdkState: INITIAL_SDK_STATE,
    permissions: {
        camera: 'unsupported',
        microphone: 'unsupported',
        screen: 'prompt',
    },
    devices: {
        cameras: [],
        microphones: [],
        hasCamera: false,
        hasMicrophone: false,
    },
    browserSupported: true,
    isMobile: false,
    error: null,
    lastViolation: null,
})

// Frozen so useSyncExternalStore gets an identity-stable server snapshot.
const SERVER_SNAPSHOT: ProctoringSnapshot = Object.freeze(createInitialSnapshot())

const SDK_STATUS_TO_PHASE: Record<ProctoringState['status'], ProctoringPhase> = {
    idle: 'idle',
    permissions_requested: 'requesting-permissions',
    ready: 'ready',
    recording: 'recording',
    stopping: 'stopping',
    stopped: 'stopped',
    cleaned: 'destroyed',
    error: 'error',
}

/** Identity that forces a new ProctoringClient when auth or tenant wiring changes. */
const sameProctoringConfig = (a: ProctoringConfigInput, b: ProctoringConfigInput) =>
    a.tenant === b.tenant &&
    a.referenceId === b.referenceId &&
    a.profile === b.profile &&
    a.api.baseUrl === b.api.baseUrl &&
    a.api.token === b.api.token

export class ProctoringManager {
    private client: ProctoringClient | null = null
    private readonly store: StateStore<ProctoringSnapshot>
    private readonly bus = new ProctoringEventBus()
    private readonly blockerController = new BlockerController({
        resumeScreenShare: () => {
            const { client } = this
            return client ? client.resumeScreenShare() : Promise.resolve({ recovered: false })
        },
        resumeDeviceStream: () => {
            const { client } = this
            return client ? client.resumeDeviceStream() : Promise.resolve({ recovered: false })
        },
    })

    private sdkUnsub: (() => void) | null = null
    private deviceChangeHandler: (() => void) | null = null

    // Off-snapshot so derivePhase() stays a pure function of sdkState.
    private transientPhase: 'initializing' | 'reconnecting' | 'destroying' | null = null
    private paused = false
    private disposed = false

    private lastConfigInput: ProctoringConfigInput | null = null
    private startPromise: Promise<void> | null = null
    private initializePromise: Promise<void> | null = null
    private destroyPromise: Promise<void> | null = null

    constructor() {
        this.store = new StateStore<ProctoringSnapshot>({
            ...createInitialSnapshot(),
            browserSupported: this.safeBrowserSupported(),
            isMobile: this.safeIsMobile(),
        })
    }

    // Arrow props keep references stable so React never re-subscribes needlessly.

    subscribe = (onStoreChange: () => void): (() => void) => this.store.subscribe(onStoreChange)

    getSnapshot = (): ProctoringSnapshot => this.store.getState()

    getServerSnapshot = (): ProctoringSnapshot => SERVER_SNAPSHOT

    getEventBus(): ProctoringEventBus {
        return this.bus
    }

    getSdk(): ProctoringClient | null {
        return this.client
    }

    getBlockerSnapshot = (): BlockerState => this.blockerController.getState()

    subscribeToBlocker = (listener: (state: BlockerState) => void): (() => void) =>
        this.blockerController.subscribe(listener)

    checkAgainBlocker = (): Promise<void> => this.blockerController.checkAgain()

    async initialize(input?: ProctoringConfigInput): Promise<void> {
        const cfg = input ?? this.lastConfigInput
        if (!cfg) return

        if (this.destroyPromise) await this.destroyPromise

        // SDK caches fetchTenantCapabilities as a one-shot promise on the client.
        // If tenant/token/profile changed (e.g. job-entry→fts, Playment→Authal),
        // rebuild — otherwise Grant keeps awaiting the old rejected promise.
        if (
            this.client &&
            this.lastConfigInput &&
            !sameProctoringConfig(this.lastConfigInput, cfg)
        ) {
            logProctoring('initialize:recreate-client', {
                prevTenant: this.lastConfigInput.tenant,
                nextTenant: cfg.tenant,
                prevProfile: this.lastConfigInput.profile,
                nextProfile: cfg.profile,
            })
            await this.destroy()
        }

        if (this.client) return
        if (this.disposed) this.disposed = false
        if (this.initializePromise) return this.initializePromise

        this.initializePromise = this.doInitialize(cfg).finally(() => {
            this.initializePromise = null
        })
        return this.initializePromise
    }

    private async doInitialize(cfg: ProctoringConfigInput): Promise<void> {
        logProctoring('initialize:start', {
            tenant: cfg.tenant,
            referenceId: cfg.referenceId,
            profile: cfg.profile,
            expectedDurationSeconds: cfg.expectedDurationSeconds,
            capabilities: cfg.capabilities,
            apiBaseUrl: cfg.api.baseUrl,
            hasApiToken: !!cfg.api.token,
        })
        const browserSupported = this.safeBrowserSupported()
        const isMobile = this.safeIsMobile()

        if (!browserSupported) {
            this.transientPhase = null
            this.sync({
                browserSupported,
                isMobile,
                error: new Error('Proctoring is not supported in this browser.'),
            })
            warnProctoring('initialize:unsupported-browser')
            return
        }

        this.disposed = false
        this.transientPhase = 'initializing'
        this.lastConfigInput = cfg
        this.blockerController.reset()
        this.blockerController.setMultiMonitorDetectionEnabled(
            cfg.capabilities.flags?.includes('multi_monitor_detected') ?? false
        )
        this.sync({ browserSupported, isMobile, error: null })

        const client = ProctoringClient.create({
            ...cfg,
            callbacks: this.buildCallbacks(),
            observability: this.buildObservability(cfg.observability),
        })
        this.client = client

        this.sdkUnsub = client.subscribe(sdkState => this.onSdkState(sdkState))

        this.registerDeviceChange()

        this.transientPhase = null
        this.sync({ sdkState: client.getState() })

        await this.refreshPermissionsAndDevices()
        logProctoring('initialize:done', { sdkStatus: client.getState().status })
    }

    /** Prompt for permissions only — no session, no recording. On denial SDK resets to `idle`; caller can retry without rebuilding the client. */
    async requestPermissions(): Promise<void> {
        if (!this.client) {
            throw new Error(
                'useProctoring: cannot request permissions before initialize — no config was provided.'
            )
        }
        const { client } = this
        const { status } = client.getState()
        if (status === 'ready' || status === 'recording') {
            logProctoring('requestPermissions:skip', { status })
            return
        }

        logProctoring('requestPermissions:start')
        return client.requestPermissions()
    }

    async start(): Promise<void> {
        if (!this.client) {
            throw new Error(
                'useProctoring: cannot start before initialize — no config was provided.'
            )
        }
        const { client } = this
        if (client.getState().status === 'recording') {
            logProctoring('start:skip-already-recording')
            return
        }
        if (this.startPromise) {
            logProctoring('start:reuse-in-flight')
            return this.startPromise
        }

        this.paused = false
        logProctoring('start:begin', { sdkStatus: client.getState().status })
        this.startPromise = this.doStart(client).finally(() => {
            this.startPromise = null
        })
        return this.startPromise
    }

    private async doStart(client: ProctoringClient): Promise<void> {
        if (client.getState().status !== 'ready') {
            logProctoring('start:request-permissions-first')
            await client.requestPermissions()
        }
        await client.start()
        logProctoring('start:done', { sdkStatus: client.getState().status })
    }

    /** Advisory pause only — the SDK has no media pause; encoder keeps running. */
    pause(): void {
        if (this.store.getState().sdkState.status !== 'recording' || this.paused) {
            return
        }
        this.paused = true
        this.sync()
        logProctoring('pause')
        this.bus.emit('pause', undefined)
    }

    resume(): void {
        if (!this.paused) return
        this.paused = false
        this.sync()
        logProctoring('resume')
        this.bus.emit('resume', undefined)
    }

    async stop(): Promise<void> {
        this.paused = false
        if (!this.client) return
        if (this.store.getState().sdkState.status !== 'recording') {
            logProctoring('stop:skip-not-recording', {
                sdkStatus: this.store.getState().sdkState.status,
            })
            this.sync()
            return
        }
        logProctoring('stop:begin')
        return this.client.stop()
    }

    async destroy(): Promise<void> {
        this.destroyPromise = this.doDestroy().finally(() => {
            this.destroyPromise = null
        })
        return this.destroyPromise
    }

    private async doDestroy(): Promise<void> {
        // Re-read client after the await — doInitialize may have set it.
        if (this.initializePromise) await this.initializePromise

        const client = this.client
        if (!client) {
            this.disposed = true
            // sync() must precede teardownRefs() so the 'destroyed' phase reaches
            // React subscribers before the store listener set is cleared.
            this.sync()
            this.teardownRefs()
            return
        }

        this.transientPhase = 'destroying'
        this.sync()
        logProctoring('destroy:begin')
        try {
            await client.cleanup()
        } finally {
            this.disposed = true
            this.transientPhase = null
            this.client = null
            this.sync({
                sdkState: INITIAL_SDK_STATE,
                error: null,
                lastViolation: null,
            })
            this.teardownRefs()
            logProctoring('destroy:done')
        }
    }

    async dispose(): Promise<void> {
        await this.destroy()
        this.blockerController.destroy()
    }

    async reset(): Promise<void> {
        const cfg = this.lastConfigInput
        await this.destroy()
        if (cfg) await this.initialize(cfg)
    }

    /**
     * @param freshInput Optional freshly-built config to use instead of
     * `lastConfigInput` — lets the caller (the hook) supply a just-refreshed
     * auth token instead of replaying whatever was captured at the last
     * `initialize()`. Falls back to `lastConfigInput` when omitted.
     */
    async reconnect(freshInput?: ProctoringConfigInput): Promise<void> {
        const cfg = freshInput ?? this.lastConfigInput
        if (!cfg) return
        this.transientPhase = 'reconnecting'
        this.sync()
        // Emit after sync so subscribers reading snapshot.phase see 'reconnecting'.
        this.bus.emit('reconnect', undefined)
        await this.destroy()
        await this.initialize(cfg)
        await this.start()
    }

    private onSdkState(sdkState: ProctoringState): void {
        logProctoring('sdk:state', {
            status: sdkState.status,
            permissionsGranted: sdkState.permissionsGranted,
        })
        const partial: Partial<ProctoringSnapshot> = { sdkState }
        if (sdkState.status === 'error' && sdkState.error) {
            partial.error = sdkState.error
            errorProctoring('sdk:error', sdkState.error)
        }
        this.sync(partial)
    }

    private buildCallbacks(): ProctoringCallbacks {
        return {
            onPermissionGranted: () => {
                logProctoring('callback:permissionGranted')
                void this.refreshPermissionsAndDevices()
                this.bus.emit('permissionGranted', undefined)
            },
            onPermissionDenied: error => {
                warnProctoring('callback:permissionDenied', error)
                this.bus.emit('permissionDenied', error)
            },
            onStreamsReady: (screen, webcam) => {
                logProctoring('callback:streamsReady', {
                    hasScreen: !!screen,
                    hasWebcam: !!webcam,
                    screenTracks: screen?.getTracks?.().length ?? 0,
                    webcamTracks: webcam?.getTracks?.().length ?? 0,
                })
                this.bus.emit('streamsReady', { screen, webcam })
            },
            onSessionCreated: result => {
                logProctoring('callback:sessionCreated', result)
                this.bus.emit('sessionCreated', result)
            },
            onRecordingStart: () => {
                logProctoring('callback:recordingStart')
                this.bus.emit('recordingStart', undefined)
            },
            onRecordingStop: () => {
                logProctoring('callback:recordingStop')
                this.bus.emit('recordingStop', undefined)
            },
            onViolation: (flag, message) => {
                const violation = { flag, message }
                warnProctoring('callback:violation', violation)
                this.sync({ lastViolation: violation })
                this.bus.emit('violation', violation)
                this.blockerController.handleViolation(flag)
            },
            onStreamHealthChange: healthy => {
                logProctoring('callback:streamHealthChange', { healthy })
                this.bus.emit('streamHealthChange', healthy)
            },
            onStreamDisconnected: reason => {
                warnProctoring('callback:streamDisconnected', reason)
                this.bus.emit('streamDisconnected', reason)
                this.blockerController.handleStreamDisconnected(reason)
            },
            onUploadProgress: progress => {
                logProctoring('callback:uploadProgress', { progress })
                this.bus.emit('uploadProgress', progress)
            },
            onError: error => {
                // onError fires for non-fatal hiccups too; phase → 'error' only when SDK status does (via onSdkState).
                errorProctoring('callback:error', error)
                this.sync({ error })
                this.bus.emit('error', error)
            },
            onCleanupComplete: () => {
                logProctoring('callback:cleanupComplete')
                this.bus.emit('cleanupComplete', undefined)
            },
        }
    }

    private buildObservability(consumer?: SDKObservabilityConfig): SDKObservabilityConfig {
        return {
            onException: (error, context) => {
                errorProctoring('observability:exception', { error, context })
                consumer?.onException?.(error, context)
                this.bus.emit('exception', { error, context })
            },
            onBreadcrumb: breadcrumb => {
                logProctoring('observability:breadcrumb', breadcrumb)
                consumer?.onBreadcrumb?.(breadcrumb)
                this.bus.emit('breadcrumb', breadcrumb)
            },
            onWarning: message => {
                warnProctoring('observability:warning', message)
                consumer?.onWarning?.(message)
            },
            onLog: message => {
                logProctoring('observability:log', message)
                consumer?.onLog?.(message)
            },
        }
    }

    private async refreshPermissionsAndDevices(): Promise<void> {
        const [permissions, devices] = await Promise.all([
            queryPermissions(this.store.getState().sdkState.status),
            enumerateDevices(),
        ])
        this.sync({ permissions, devices })
    }

    private registerDeviceChange(): void {
        // `mediaDevices` is absent in older browsers / non-DOM (SSR) contexts.
        const md = navigator.mediaDevices as MediaDevices | undefined
        if (!md) return
        this.deviceChangeHandler = () => {
            void this.refreshPermissionsAndDevices()
        }
        md.addEventListener('devicechange', this.deviceChangeHandler)
    }

    private unregisterDeviceChange(): void {
        const md = navigator.mediaDevices as MediaDevices | undefined
        if (this.deviceChangeHandler && md) {
            md.removeEventListener('devicechange', this.deviceChangeHandler)
        }
        this.deviceChangeHandler = null
    }

    private teardownRefs(): void {
        this.sdkUnsub?.()
        this.sdkUnsub = null
        this.unregisterDeviceChange()
        this.client = null
        this.paused = false
    }

    private derivePhase(sdkState: ProctoringState): ProctoringPhase {
        if (this.transientPhase) return this.transientPhase
        if (!this.client) return this.disposed ? 'destroyed' : 'uninitialized'
        if (this.paused && sdkState.status === 'recording') return 'paused'
        return SDK_STATUS_TO_PHASE[sdkState.status]
    }

    private sync(partial: Partial<ProctoringSnapshot> = {}): void {
        const sdkState = partial.sdkState ?? this.store.getState().sdkState
        this.store.setState({ ...partial, phase: this.derivePhase(sdkState) })
        this.bus.emit('stateChange', this.store.getState())
    }

    private safeBrowserSupported(): boolean {
        try {
            return isBrowserSupported()
        } catch {
            return false
        }
    }

    private safeIsMobile(): boolean {
        try {
            return isMobileDevice()
        } catch {
            return false
        }
    }
}
